import { Dialog, DialogProps, Portal } from "@mui/material";
import React from "react";

// Re-export DialogActions and DialogContent from @mui/material, so that we don't have to import them
// separately in the components that use Modal.
export {
  DialogActions as ModalActions,
  DialogContent as ModalContent,
  DialogTitle as ModalTitle,
  DialogContentText as ModalContentText,
} from "@mui/material";

import { useDidMountEffect } from "@/features/shared/hooks";

export type ModalComponent =
  | undefined
  | {
      open: () => void;
      close: () => void;
    };

type Props = {
  children: React.ReactNode;
  // @deprecated
  triggerElement?: React.ReactElement;
  onClose?: () => void;
} & Omit<DialogProps, "open" | "onClose">;

const Modal: React.ForwardRefRenderFunction<ModalComponent, Props> = (
  { children, triggerElement, onClose, ...restProps },
  ref,
) => {
  const [open, setOpen] = React.useState(false);

  React.useImperativeHandle(ref, () => ({
    open: () => {
      setOpen(true);
    },
    close: () => {
      setOpen(false);
    },
  }));

  useDidMountEffect(() => {
    if (!open && onClose) {
      onClose();
    }
  }, [open]);

  return (
    <>
      {triggerElement &&
        React.cloneElement(triggerElement, {
          onClick: () => {
            setOpen(true);
            triggerElement.props?.onClick?.();
          },
        })}
      <Portal>
        <Dialog open={open} fullWidth maxWidth="md" {...restProps}>
          {children}
        </Dialog>
      </Portal>
    </>
  );
};

export default React.forwardRef(Modal);
